import React, { useCallback, useRef, useState } from 'react';

import Icon from '@amzn/awsui-components-console/icon';
import Tooltip from '@amzn/awsui-components-console/tooltip';

interface InfoTooltipProps {
  /**
   * Explanatory text shown on hover or focus.
   *
   * Also applied as the trigger's `aria-label`: Cloudscape's Tooltip content is
   * not exposed to assistive technologies, so the trigger must carry an
   * accessible name matching the tooltip text.
   */
  readonly content: string;
  /** Placement relative to the icon. Defaults to 'top'. */
  readonly position?: 'top' | 'right' | 'bottom' | 'left';
}

/**
 * Shared info icon that reveals explanatory text on hover or keyboard focus.
 *
 * Follows the interaction contract Cloudscape's Tooltip documents: the trigger
 * is focusable, owns the visibility state, and dismisses on pointer leave, blur,
 * or Escape. Pointer events (rather than mouse events) are used as documented,
 * so touch and pen input work too.
 *
 * The Tooltip renders inside the trigger span so the component stays a single
 * child — a Fragment would be split into separate items by SpaceBetween.
 */
const InfoTooltip: React.FC<InfoTooltipProps> = ({
  content,
  position = 'top',
}) => {
  const triggerRef = useRef<HTMLSpanElement>(null);
  const [visible, setVisible] = useState(false);

  const getTrack = useCallback(() => triggerRef.current, []);
  const show = useCallback(() => setVisible(true), []);
  const hide = useCallback(() => setVisible(false), []);

  return (
    <span
      ref={triggerRef}
      tabIndex={0}
      role="img"
      aria-label={content}
      onPointerEnter={show}
      onPointerLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      <Icon name="status-info" size="small" variant="link" />
      {visible && (
        <Tooltip
          content={content}
          getTrack={getTrack}
          position={position}
          onEscape={hide}
        />
      )}
    </span>
  );
};

export default InfoTooltip;
